import { useTransition, type FC } from 'react';
import { useEffect, useState } from 'react';
import type { IProductVM } from '@/react/view-models/ProductVM';
import { ContentType } from '@/services/isomorphic/ContentService/ContentType';
import {
  ContentService,
  type Content
} from '@/services/isomorphic/ContentService';
import { isNullOrEmpty } from '@/utils/null-utils';
import { DirectContent } from '@/react/components/content/core/DirectContent';
import SkeletonLoader from '@/react/components/utility/SkeletonLoader';
import { CommonErrorBoundary } from '../../errors/boundaries/CommonErrorBoundary';

import S from './styles.module.scss';

export interface IBelowATCContentProps {
  /** The product view model. */
  product: IProductVM;
}

/** The product franchise details section on the PDP. */
export const BelowATCContent: FC<IBelowATCContentProps> = ({ product }) => {
  const contentAssetIncludeBelowATC =
    product?.details.contentAssetIncludeBelowATC;
  const [content, setContent] = useState<Promise<Content> | null>(null);
  const [isPending, startTransition] = useTransition();

  /**
   * **NOTE**: Ideally, this content should be fetched by a server component and
   * streamed to the client. However, the content identifier may vary between
   * product variations, and currently navigations between variations are
   * shallow, meaning Next.js won't fetch the updated RSC. So we must fetch it
   * in an effect.
   */
  useEffect(() => {
    setContent(null);

    if (isNullOrEmpty(contentAssetIncludeBelowATC)) {
      return;
    }

    startTransition(() => {
      setContent(
        ContentService.getContent({
          type: ContentType.Fragment,
          id: contentAssetIncludeBelowATC
        })
      );
    });
  }, [contentAssetIncludeBelowATC]);

  if (isNullOrEmpty(contentAssetIncludeBelowATC)) {
    return null;
  }

  return (
    <article className={S.container}>
      {isPending || content == null ? (
        <SkeletonLoader className={S.skeletonLoader} />
      ) : (
        <CommonErrorBoundary fallback={null}>
          <DirectContent content={content} />
        </CommonErrorBoundary>
      )}
    </article>
  );
};
